import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CampaignErrorToastProps {
    isVisible: boolean;
    message: string;
    onDismiss: () => void;
    autoHideDuration?: number;
}

export function CampaignErrorToast({
    isVisible,
    message,
    onDismiss,
    autoHideDuration = 5000,
}: CampaignErrorToastProps) {
    // Auto-hide the toast
    useEffect(() => {
        if (isVisible && autoHideDuration > 0) {
            const timer = setTimeout(onDismiss, autoHideDuration);
            return () => clearTimeout(timer);
        }
    }, [isVisible, autoHideDuration, onDismiss]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 0, y: -100, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -100, scale: 0.8 }}
                    className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md mx-auto"
                >
                    <div className="bg-red-500/90 backdrop-blur-xl rounded-xl border border-red-400/30 shadow-2xl p-4 text-white">
                        <div className="flex items-start gap-3">
                            {/* Error Icon */}
                            <div className="flex-shrink-0 w-6 h-6 mt-0.5">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="w-full h-full text-red-200"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                    <path
                                        d="15 9l-6 6M9 9l6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>

                            {/* Error Content */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold mb-1">
                                    Campaign Loading Error
                                </h3>
                                <p className="text-sm text-red-100 leading-relaxed">
                                    {message}
                                </p>
                            </div>

                            {/* Close Button */}
                            <button
                                onClick={onDismiss}
                                className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                                aria-label="Dismiss error"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="w-4 h-4 text-red-200"
                                >
                                    <path
                                        d="18 6L6 18M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default CampaignErrorToast;
